import {Component, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {NgForm} from '@angular/forms';
import {first} from 'rxjs/operators';
import {ToastrService} from 'ngx-toastr';
import {TranslateService} from '@ngx-translate/core';
import {BehaviorSubject} from 'rxjs';
import {FloatingButtonAction, TypeAction} from '@share/component/floating-button/floating-button';
import {PageHeaderItem} from '@share/component/page-header/page-header';
import {LoginService} from '@share/login/login.service';
import {Session} from '@domain/security/session';
import {TypeProfil} from '@domain/user/user';
import {TypeCodeErreur} from '@domain/common/http/result';
import {Portee} from '@domain/portee/portee';
import {MessageNotif} from '@domain/notification/message-notif';
import {Role} from '@domain/workflow/role';
import {Statut} from '@domain/workflow/statut';
import {RelanceDetail} from '@domain/relance/relance-detail';
import {MatLegacyDialog as MatDialog} from "@angular/material/legacy-dialog";
import {SelectRelanceStatutsPopinComponent} from "./select-relance-statuts-popin/select-relance-statuts-popin.component";
import {SelectRelanceRolesPopinComponent} from "./select-relance-roles-popin/select-relance-roles-popin.component";
import {NotificationRelancesService} from '../notification-relances.service';
import {ConfirmService} from "@share/component/confirmation/confirm.service";
import {filter} from "rxjs/operators";

/**
 * Détail des relances
 */
@Component({
    host: {'data-test-id': 'relance-detail'},
    templateUrl: './relance-detail.component.html'
})
export class RelanceDetailComponent implements OnInit {

    /** Référence vers le formulaire */
    @ViewChild('formRelance', {static: false}) formRelance: NgForm;

    /** Données session (droits) */
    session: Session;

    /** Mode écran */
    idRelance: number = 0;
    isCreation: boolean = true;

    /** Listes */
    listePortee: Portee[] = [];
    listeMessage: MessageNotif[] = [];
    listeRole: Role[] = [];
    listeStatut: Statut[] = [];

    /** Sélections de rôle et statut */
    rolesSelectionnes: Role[] = [];
    statutsSelectionnes: Statut[] = [];

    /** Modèle relance */
    relance: RelanceDetail = {
        idRelance: 0,
        actif: true,
        param1: '',
        param2: ''
    };

    /** Liste des onglets */
    listeTabItems: Array<PageHeaderItem> = [{
        code: 'GENERALITES',
        libelle: 'admin.bibliotheque.notifications.relances.generalites',
        selected: true
    }];

     /** Liste des actions */
    listeActions: BehaviorSubject<Array<FloatingButtonAction>> = new BehaviorSubject<Array<FloatingButtonAction>>(null);

     /** UI */
    isLoading: boolean = false;
    isSaving: boolean = false;

    /** Options pour l'affichage/édition de la requête SQL */
    codeMirrorOptions: any = {
        theme: 'material',
        mode: 'text/x-mssql',
        lineNumbers: true,
        lineWrapping: true,
        readOnly: false
    };

    /** Indique si le SQL est vide (champ requis) */
    get isSqlVide(): boolean {
        return !this.relance?.sql || !this.relance.sql.trim();
    }

    /** Indique si le SQL est invalide */
    get isSqlInvalid(): boolean {
        return this.isSqlVide;
    }

    /** Indique si le bouton Enregistrer doit être inactif */
    get isEnregistrerDisabled(): boolean {
        return this.isSaving || !this.formRelance?.valid || this.isSqlVide;
    }

    /** Titre dynamique */
    get titrePage(): string {
        return this.isCreation
            ? this.translateService.instant('admin.bibliotheque.notifications.relances.titreCreation')
            : this.translateService.instant('admin.bibliotheque.notifications.relances.titreModification');
    }

    /** Indique si l'utilisateur est sous-admin */
    get estSousAdmin(): boolean {
        return this.session?.user?.fonction === TypeProfil.SOUS_ADMINISTRATEUR;
    }

    /** Champ SQL éditable seulement pour admin */
    get isConditionSqlReadonly(): boolean {
        return this.estSousAdmin;
    }

    /**
     * Constructeur
     *
     * @param route Route activée
     * @param router Routeur Angular
     * @param loginService Service de login
     * @param notificationRelancesService Service de relances
     * @param translateService Service de traduction
     * @param toastrService Service de notification
     * @param matDialog Service des popin
     * @param confirmService Service de confirmation
     */
    constructor(private route: ActivatedRoute,
                private router: Router,
                private loginService: LoginService,
                private notificationRelancesService: NotificationRelancesService,
                private translateService: TranslateService,
                private toastrService: ToastrService,
                private matDialog: MatDialog,
                private confirmService: ConfirmService) {
    }

    /**
     * Initialisation
     */
    ngOnInit(): void {
        //Récupération session
        this.session = this.loginService.getSession();

        //Récupération idRelance (0 => création)
        const idParam = this.route.snapshot.paramMap.get('idRelance');
        this.idRelance = Number(idParam ?? 0);
        this.isCreation = this.idRelance === 0;

        //Chargement écran
        this.load();

        //Initialisation des actions
        this.listeActions.next([{
            type: TypeAction.PRIMARY,
            icone: 'nio icon-sauvegarde',
            libelle: 'global.actions.enregistrer',
            doAction: () => this.save(),
            isDisabled: () => this.isEnregistrerDisabled
        },{
            type: TypeAction.SECONDARY,
            icone: 'material-symbols-rounded',
            iconeName: 'rule_settings',
            libelle: 'global.actions.executer',
            isVisible: () => !this.isCreation,
            doAction: () => this.execute()
        },{
            type: TypeAction.SECONDARY,
            icone: 'nio icon-suppression',
            libelle: 'global.actions.supprimer',
            isVisible: () => !this.isCreation && !this.estSousAdmin,
            doAction: () => this.delete()
        }]);
    }

    /**
     * Ouverture du popin statuts
     */
    onClickStatut(): void {
        //Synchronisation du modèle avant ouverture de la popin
        this.syncStatutsToRelance();

        //Ouverture de la popin de sélection des statuts avec la liste complète
        this.matDialog.open(SelectRelanceStatutsPopinComponent, {
            data: {
                relance: this.relance,
                listeStatuts: this.listeStatut
            }
        })
            //Au retour, mise à jour de la sélection + resynchronisation du modèle
            .afterClosed().subscribe((listeStatuts: Statut[]) => {
            if (listeStatuts) {
                //Mise à jour des statuts sélectionnés
                this.statutsSelectionnes = listeStatuts;

                //Synchronisation de la sélection
                this.syncStatutsToRelance();
            }
        });
    }

    /**
     * Ouverture du popin rôles
     */
    onClickRole(): void {
        //Synchronisation du modèle avant ouverture de la popin
        this.syncRolesToRelance();

        //Ouverture de la popin de sélection des rôles avec la liste complète
        this.matDialog.open(SelectRelanceRolesPopinComponent, {
            data: {
                relance: this.relance,
                listeRoles: this.listeRole
            }
        })
            //Au retour, mise à jour de la sélection + resynchronisation du modèle
            .afterClosed().subscribe((listeRoles: Role[]) => {
            if (listeRoles) {
                //Mise à jour des rôles sélectionnés
                this.rolesSelectionnes = listeRoles;

                //Synchronisation de la sélection
                this.syncRolesToRelance();
            }
        });
    }

    /**
     * Chargement des données (listes + relance)
     */
    private load(): void {
        //indicateur de chargement
        this.isLoading = true;

        //Chargement des données
        this.notificationRelancesService.loadRelance(this.idRelance)
            .pipe(first())
            .subscribe({
                next: result => {
                    //On récupère les données brutes
                    const data: any = result?.data;

                    //Affectation champ par champ
                    this.listePortee = (data?.listePortee ?? []) as Portee[];
                    this.listeMessage = (data?.listeMessage ?? []) as MessageNotif[];
                    this.listeRole = (data?.listeRole ?? []) as Role[];
                    this.listeStatut = (data?.listeStatut ?? []) as Statut[];

                    //En création
                    this.relance = (data?.relance ?? {idRelance: 0, actif: true, param1: '', param2: ''}) as RelanceDetail;

                    //Sécurité : si on a un id en URL, on le force
                    this.relance.idRelance = this.idRelance;

                    //Sécurisation des champs optionnels (absents du JSON si vides)
                    if (this.relance.param1 == null) {
                        this.relance.param1 = '';
                    }
                    if (this.relance.param2 == null) {
                        this.relance.param2 = '';
                    }

                    //Initialisation sélection rôles depuis le modèle renvoyé
                    const idsRoles = (this.relance?.listeRoleRelance ?? []).map(rr => rr?.idRole).filter(Boolean);
                    this.rolesSelectionnes = (this.listeRole ?? []).filter(r => idsRoles.includes(r.idRole));

                    //Initialisation sélection statuts depuis le modèle renvoyé
                    const idsStatuts = (this.relance?.listeStatut ?? []).map(s => s?.idStatut).filter(Boolean);
                    this.statutsSelectionnes = (this.listeStatut ?? []).filter(s => idsStatuts.includes(s.idStatut));

                    //Fin chargement
                    this.isLoading = false;

                },
                error: () => {
                    this.isLoading = false;
                    this.toastrService.error(this.translateService.instant('global.errors.chargement'));
                }
            });
    }

    /**
     * Enregistrement
     */
    save(): void {
        //indicateur de sauvegarde
        this.isSaving = true;

        //Reconstruction du format attendu par le back pour les rôles
        this.relance.listeRoleRelance = (this.rolesSelectionnes ?? []).map(role => ({
            idRelance: this.relance.idRelance,
            idRole: role.idRole,
            actif: true,
            role: role
        }));

        //Reconstruction du format attendu par le back pour les statuts
        this.relance.listeStatut = (this.statutsSelectionnes ?? []).map(statut => statut);

        //Sauvegarde de la relance
        this.notificationRelancesService.saveRelance(this.relance)
            .pipe(first())
            .subscribe({
                next: res => {
                    //Fin de la sauvegarde
                    this.isSaving = false;

                    //id de la relance créée
                    const idRelanceCree = res?.data?.idRelance;

                    //Si création on met à jour l'id de la relance
                    if (idRelanceCree) {
                        this.idRelance = idRelanceCree;
                        this.relance.idRelance = idRelanceCree;
                        this.isCreation = false;
                    }

                    //Si succès
                    if (res?.codeErreur === TypeCodeErreur.NO_ERROR) {
                        this.toastrService.success(this.translateService.instant('global.success.enregistrement'));

                        //Après création, on redirige vers la relance créée si le back a mis à jour l'id
                        const id = idRelanceCree ?? this.relance?.idRelance ?? this.idRelance;
                        this.router.navigate(['/Admin/Bibliotheque/Notification/Relances', id]);
                    } else {
                        //Si erreur
                        TypeCodeErreur.showError(res?.codeErreur, this.translateService, this.toastrService);
                    }
                },
                error: () => {
                    this.isSaving = false;
                    this.toastrService.error(this.translateService.instant('global.errors.enregistrement'));
                }
            });
    }

    /**
     * Retour liste
     */
    cancel(): void {
        this.router.navigate(['/Admin/Bibliotheque/Notification/Relances']);
    }

    /**
     * Synchronisation statuts sélectionnés
     */
    private syncStatutsToRelance(): void {
        this.relance.listeStatut = (this.statutsSelectionnes ?? []).map(s => s);
    }

    /**
     * Synchronisation rôles sélectionnés
     */
    private syncRolesToRelance(): void {
        this.relance.listeRoleRelance = (this.rolesSelectionnes ?? []).map(role => ({
            idRelance: this.relance.idRelance,
            idRole: role.idRole,
            actif: true,
            priorite: -1,
            role: role
        }));
    }

    /**
     * Suppression d'une relance
     */
    delete(): void {
        //confirmation de suppression
        this.confirmService.showConfirm(this.translateService.instant('global.suppression.confirmation'))
            .pipe(filter(isConfirmed => isConfirmed))
            .subscribe(() => {
                //indicateur de suppression
                this.isSaving = true;

                //Suppression de la relance
                this.notificationRelancesService.deleteRelance(this.relance)
                    .pipe(first())
                    .subscribe({
                        next: res => {
                            //Fin de la suppression
                            this.isSaving = false;

                            //Si succès
                            if (res?.codeErreur === TypeCodeErreur.NO_ERROR) {
                                //toast de succès
                                this.toastrService.success(this.translateService.instant('global.success.suppression'));

                                //Redirection vers la liste des relances
                                this.router.navigate(['/Admin/Bibliotheque/Notification/Relances']);
                            } else {
                                //Si erreur
                                TypeCodeErreur.showError(res?.codeErreur, this.translateService, this.toastrService);
                            }
                        },
                        error: () => {
                            this.isSaving = false;
                            this.toastrService.error(this.translateService.instant('global.errors.suppression'));
                        }
                    });
            });
    }

    /**
     * Exécution d'une relance
     */
    execute(): void {
        //Début d'exécution
        this.isSaving = true;

        //Exécution de la relance
        this.notificationRelancesService.executeRelance(this.relance.idRelance)
            .pipe(first())
            .subscribe({
                next: res => {
                    //Fin de l'exécution
                    this.isSaving = false;

                    //Si succès
                    if (res?.codeErreur === TypeCodeErreur.NO_ERROR) {
                        //toast de succès
                        this.toastrService.success(this.translateService.instant('global.success.generic'));
                    } else {
                        //toast d'erreur
                        TypeCodeErreur.showError(res?.codeErreur, this.translateService, this.toastrService);
                    }
                },
                error: () => {
                    this.isSaving = false;
                    this.toastrService.error(this.translateService.instant('global.errors.generic'));
                }
            });
    }
}